'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import type { BreadcrumbsProps } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useState, type PropsWithChildren, useRef, useEffect } from 'react';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { usePathname } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type PageHeaderProps = {
  heading?: string;
  /** The route owns the primary H1 by default; reusable/secondary headers must opt out. */
  headingRole?: 'primary' | 'section' | 'presentation';
  description?: string;
  headerLink?: React.ReactNode;
  mobileAction?: React.ReactNode;
  beforeDescription?: React.ReactNode;
  afterHeading?: React.ReactNode;
  afterDescription?: React.ReactNode;
  variant?: 'default' | 'center';
  hideOverlay?: boolean;
  className?: {
    wrapper?: string;
    container?: string;
    content?: string;
    header?: string;
    description?: string;
    breadcrumbs?: BreadcrumbsProps['className'];
    heading_row?: string;
  };
  breadcrumb?: Breadcrumb;
};

const MOBILE_COLLAPSED_LINES = 2;
const DESKTOP_COLLAPSED_LINES = 3;

export function PageHeader({
  variant,
  hideOverlay = false,
  ...props
}: PropsWithChildren<PageHeaderProps>) {
  const { t } = useTranslations();
  const [isExtended, setShowMore] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  const descriptionRef = useRef<HTMLDivElement>(null);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const HeadingTag =
    props.headingRole === 'section'
      ? 'h2'
      : props.headingRole === 'presentation'
        ? 'div'
        : 'h1';

  useEffect(() => {
    if (templateSettings?.category?.category_description_always_open?.enabled) {
      setShowMore(true);
      setShowToggle(false);
      return;
    }

    const getCollapsedLines = () =>
      window.matchMedia('(min-width: 640px)').matches
        ? DESKTOP_COLLAPSED_LINES
        : MOBILE_COLLAPSED_LINES;

    const checkDescriptionLines = () => {
      const el = descriptionRef.current;
      if (!el || !props.description) return;

      const collapsed = getCollapsedLines();
      const style = window.getComputedStyle(el);

      const lineHeight =
        Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.2;

      const probe = document.createElement('div');
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.zIndex = '-1';
      probe.style.left = '0';
      probe.style.top = '0';
      probe.style.width = `${el.clientWidth}px`;
      probe.style.fontFamily = style.fontFamily;
      probe.style.fontSize = style.fontSize;
      probe.style.fontWeight = style.fontWeight;
      probe.style.letterSpacing = style.letterSpacing;
      probe.style.lineHeight = style.lineHeight;
      probe.style.whiteSpace = 'normal';
      probe.style.display = 'block';

      probe.innerHTML = props.description;
      document.body.appendChild(probe);

      const fullHeight = probe.getBoundingClientRect().height;
      document.body.removeChild(probe);

      const fullLines = fullHeight / lineHeight;

      setShowToggle(fullLines > collapsed + 0.15);
    };

    checkDescriptionLines();
    window.addEventListener('resize', checkDescriptionLines);
    return () => window.removeEventListener('resize', checkDescriptionLines);
  }, [props.description, templateSettings?.category?.category_description_always_open?.enabled]);

  const pathname = usePathname();

  const isSearchPage = pathname === ROUTES.SEARCH();

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-none bg-background py-0 sm:rounded-lg sm:pt-4',
        (props.breadcrumb && props.heading) ||
          (props.breadcrumb && props.description) ||
          (props.breadcrumb && props.children)
          ? 'gap-4'
          : '',
        'ea-homepagepage-header',
        templateSettings?.realty_header && 'bg-transparent',
        props.className?.wrapper,
      )}
    >
      {props.breadcrumb && (
        <Breadcrumbs
          className={{
            ...props.className,
            wrapper: cn(
              'mx-auto w-full max-w-screen-xl',
              'ea-homepage-header__breadcrumbs',
              templateSettings?.jobportal_header && 'bg-transparent',
              props.className?.wrapper,
            ),
          }}
          breadcrumb={props.breadcrumb}
        />
      )}

      <Container
        size="xl"
        className={cn(
          'flex flex-col rounded-none',
          'ea-homepage-header__container',
          {
            'items-center': variant === 'center',
            'items-start': variant === 'default',
          },
          props.className?.container,
        )}
      >
        {props.children}

        <div
          className={cn(
            'flex w-full flex-col gap-4',
            'ea-homepage-header__content',
            props.className?.content,
          )}
        >
          <div
            className={cn(
              'flex flex-row items-center justify-between gap-4',
              'ea-homepage-header__heading-row',
              props.className?.heading_row,
            )}
          >
            {props.heading && (
              <div className="flex flex-row gap-2">
                <HeadingTag
                  {...(props.headingRole === 'primary' || !props.headingRole
                    ? { 'data-ea-primary-heading': true }
                    : {})}
                  className={cn(
                    'text-[32px] font-bold',
                    'ea-homepage-header__heading',
                    props.className?.header,
                  )}
                >
                  {props.heading}
                </HeadingTag>
                {props.afterHeading && (
                  <div className="flex items-center gap-2">{props.afterHeading}</div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button asChild variant="link" className="px-0">
                {props.headerLink}
              </Button>
              <div className="flex items-center md:hidden">{props.mobileAction}</div>
            </div>
          </div>

          {props.description && (
            <div className="relative flex flex-col items-start">
              {props.beforeDescription}

              <HtmlRenderer
                refDiv={descriptionRef}
                className={cn(
                  'h-auto min-h-min overflow-hidden text-sm text-muted-foreground transition-all duration-300 ease-in-out',
                  !isExtended ? 'line-clamp-2 sm:line-clamp-3' : 'line-clamp-none',
                  'ea-homepage-header__description',
                  props.className?.description,
                )}
                html={props.description}
              />
              {!isSearchPage && (
                <>
                  {showToggle && (
                    <>
                      <div className="relative z-10 flex w-full justify-center sm:hidden">
                        <Button
                          variant="link"
                          className="px-0"
                          onClick={() => setShowMore(v => !v)}
                        >
                          {isExtended ? t('text528') : t('text529')}
                        </Button>
                      </div>

                      <Button
                        variant="link"
                        className="relative z-10 mx-auto hidden px-0 sm:flex"
                        onClick={() => setShowMore(v => !v)}
                      >
                        {isExtended ? t('text528') : t('text529')}
                      </Button>
                    </>
                  )}
                  {showToggle && !hideOverlay && (
                    <div
                      className={cn(
                        'pointer-events-none absolute bottom-0 left-0 z-[2] h-40 w-full bg-gradient-to-t from-background to-transparent sm:-bottom-0 sm:h-20',
                        {
                          hidden: isExtended,
                          block: !isExtended,
                        },
                      )}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
